import {
  type BufferGeometry,
  InstancedMesh,
  Line,
  type Material,
  Mesh,
  type Object3D,
  Points
} from "three";

const sharedGeometries = new WeakSet<BufferGeometry>();
const sharedMaterials = new WeakSet<Material>();

export function markSharedModelResources(object: Object3D) {
  object.traverse((child) => {
    if (!isResourceObject(child)) {
      return;
    }
    sharedGeometries.add(child.geometry);
    for (const material of toMaterialArray(child.material)) {
      sharedMaterials.add(material);
    }
  });
}

export function markSharedModelMaterial(material: Material) {
  sharedMaterials.add(material);
  return material;
}

export function cloneSharedModel(source: Object3D) {
  const clone = source.clone(true);
  clone.traverse((child) => {
    if (child instanceof Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });
  return clone;
}

export function disposeObject3DResources(object: Object3D) {
  const ownedGeometries = new Set<BufferGeometry>();
  const ownedMaterials = new Set<Material>();

  object.traverse((child) => {
    if (!isResourceObject(child)) {
      return;
    }
    if (child instanceof InstancedMesh) {
      child.dispose();
    }
    if (!sharedGeometries.has(child.geometry)) {
      ownedGeometries.add(child.geometry);
    }
    for (const material of toMaterialArray(child.material)) {
      if (!sharedMaterials.has(material)) {
        ownedMaterials.add(material);
      }
    }
  });

  ownedGeometries.forEach((geometry) => {
    geometry.dispose();
  });
  ownedMaterials.forEach((material) => {
    material.dispose();
  });
}

function toMaterialArray(material: Material | Material[]) {
  return Array.isArray(material) ? material : [material];
}

function isResourceObject(object: Object3D): object is Mesh | Line | Points {
  return object instanceof Mesh || object instanceof Line || object instanceof Points;
}
